import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { equals, includes, isEmpty, pluck, prop, propOr, reject } from 'ramda';
import booleanOptions, { getCaisseFromValue } from '@@viewCommonComponents/forms/options';
import { useFormContext } from 'react-hook-form';
import { dateHasConstraints, formatZipCode, isRequiredBoolean, isRequiredBooleanTrue, isRequiredInput, isRequiredSelect, landlinePhoneNumberRegex, mobilePhoneNumberRegex, nameIsValid, numberIsBetween, zipCodeRules } from '@@viewHelpers/validations';
import { ControlledDatePicker, ControlledInput, ControlledPhoneInput, ControlledRadioButton, ControlledSelect, ControlledSSInput } from '@@viewCommonComponents/controlled';
import { civilityOptions } from '@@viewCommonComponents/forms/options/index';
import TwinRanksSelect from '@@viewCommonComponents/TwinRanksSelect';
import useFetchCitiesQuery from '../../hooks/useFetchCitiesQuery.js';
import useGetLegalIndicatorsQuery from '../../hooks/useGetLegalIndicatorsQuery';
import moment from 'moment';
import { DashedTitle, InfoMessage, Message } from '@fasstech/middle-design-system';
import { FormLayout } from '@@viewCommonComponents/forms/Layouts.jsx';
import CustomerIsCSS from '../../helpers/CustomerIsCSS.jsx';
import ControlledCheckbox from '@@viewCommonComponents/controlled/ControlledCheckbox.jsx';
import * as EmailValidator from 'email-validator';
import SimulationCustomerGranteesExtraInfoMessage from '../../extra/SimulationCustomerGranteesExtraInfoMessage.jsx';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import { offerCodeEnum } from '@@viewEnums';
import useMailMobileLandlineCrossValidation from '../../../../_common/client/hooks/useMobileLandlineCrossValidation.mjs';
import useBirthDate from './hooks/useBirthDate.jsx';
import getExtraAdherentConsentementTelText from '../../extra/getExtraAdherentConsentementTelText.js';
import { isNotNilOrEmpty } from '@@serverHelpers';
import SimulationCustomerGranteesExtraCompanyCreationDate from '../../extra/SimulationCustomerGranteesExtraCompanyInformation.jsx';

const CustomerForm = ({
  jobs,
  professionalSituations,
  regimeObligatoire,
  socioProfessionalCategories,
  activities,
  getMiddleText,
  getMiddleBoolean,
  getMiddleNumber,
  phoneIndicatives,
  postalCodeFranceMetro,
  postalCodeDROM,
  selectedTab,
  displayAdditionalInfo,
  displayActivityInfo,
  telEmailValidationErrorMessage
}) => {
  const form = useFormContext();
  const {
    watch,
    formState: { errors }
  } = form;
  const { context } = useProcess();

  const postalCode = watch('customer.addresses.0.zipCode');
  const shouldSkipFetchCities = !getMiddleBoolean('ADHERENT_VILLE_EST_NECESSAIRE') || (getMiddleBoolean('ADHERENT_VILLE_EST_NECESSAIRE') && !getMiddleBoolean('ADHERENT_VILLE_EST_DEMANDE_DONNEES_CONTRAT'));
  const { fetchCities: cities, refetch: refetchCities } = useFetchCitiesQuery({
    variables: {
      postalCode
    },
    skip: shouldSkipFetchCities
  });

  const effectDate = watch('offer.effectDate');
  const { getLegalIndicators: legalIndicators, loading: isFetchingIndicators } =
    useGetLegalIndicatorsQuery({
      variables: {
        effectDate: moment(effectDate).format('DD/MM/YYYY')
      },
      skip: context.offerCode !== offerCodeEnum.SELF_EMPLOYED_WORKER_PROTECTION
    });

  useEffect(() => {
    if (!shouldSkipFetchCities && postalCode) {
      refetchCities({ postalCode });
    }
  }, [postalCode, shouldSkipFetchCities]);

  useEffect(() => {
    if (!isFetchingIndicators) {
      form.trigger('customer.finances.0.annualIncome');
    }
  }, [isFetchingIndicators]);

  const emailConsent = form.watch('customer.GDPR.email');
  const phoneConsent = form.watch('customer.GDPR.phone');
  useEffect(() => {
    form.trigger('customer.GDPR.email');
    form.trigger('customer.GDPR.phone');
  }, [emailConsent, phoneConsent]);

  const {
    minBirthDate,
    maxBirthDate,
    customerAgeTooltipText,
    minCustomerAge,
    maxCustomerAge,
    customerAgeInfoText
  } = useBirthDate({
    getMiddleNumber,
    getMiddleText,
    effectDate,
    errors,
    context
  });

  const excludedCaissesPro = pluck('CAISSES')(
    getMiddleText('ADHERENT_CAISSES_PRO_EXCLUES')
  );

  const filteredJobs = reject((job) =>
    includes(prop('caisse', job), excludedCaissesPro)
  )(jobs || []);

  const caisseProValue = getCaisseFromValue(
    watch('customer.CSPs.0.externalId'),
    filteredJobs || []
  );

  const mobileNumber = form.watch('customer.phones.0.number');
  const landlineNumber = form.watch('customer.phones.1.number');
  const aPhoneNumberHasAValueAndIsValid =
    (!isEmpty(mobileNumber) && mobilePhoneNumberRegex.test(mobileNumber)) ||
    (!isEmpty(landlineNumber) && landlinePhoneNumberRegex.test(landlineNumber));
  const email = form.watch('customer.emails.0.value');
  const emailHasAValueAndIsValid =
    !isEmpty(email) && EmailValidator.validate(email);

  const getMailMobileLandlineInputRules = useMailMobileLandlineCrossValidation(
    form,
    getMiddleBoolean
  );

  const displayCommercialConsent =
    getMiddleBoolean('ADHERENT_CONSENTEMENT_COMMERCIAL_EST_NECESSAIRE') &&
    getMiddleBoolean(
      'ADHERENT_CONSENTEMENT_COMMERCIAL_EST_DEMANDE_DONNEES_CONTRAT'
    );

  return (
    <FormLayout
      className={`${selectedTab !== 0 || isFetchingIndicators ? 'hidden' : ''}`}
    >
      <DashedTitle>
        {getMiddleText('ADHERENT_GENERAL_VIEW_SUBTITLE')}
      </DashedTitle>
      <ControlledRadioButton
        name="customer.civility"
        rules={isRequiredSelect(
          civilityOptions,
          getMiddleBoolean('ADHERENT_CIVILITE_EST_OBLIGATOIRE')
        )}
        form={form}
        options={civilityOptions}
        data-testid="ADHERENT_CIVILITE"
        isDisplayed={
          getMiddleBoolean('ADHERENT_CIVILITE_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_CIVILITE_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <ControlledInput
        name="customer.lastName"
        label={getMiddleText('ADHERENT_NOM_LABEL')}
        form={form}
        rules={nameIsValid(getMiddleBoolean('ADHERENT_NOM_EST_OBLIGATOIRE'))}
        data-testid="ADHERENT_NOM"
        isDisplayed={
          getMiddleBoolean('ADHERENT_NOM_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_NOM_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <ControlledInput
        name="customer.firstName"
        label={getMiddleText('ADHERENT_PRENOM_LABEL')}
        form={form}
        rules={nameIsValid(getMiddleBoolean('ADHERENT_PRENOM_EST_OBLIGATOIRE'))}
        data-testid="ADHERENT_PRENOM"
        isDisplayed={
          getMiddleBoolean('ADHERENT_PRENOM_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_PRENOM_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <ControlledInput
        name="customer.addresses.0.zipCode"
        label={getMiddleText('ADHERENT_CODE_POSTAL_LABEL')}
        form={form}
        rules={zipCodeRules(
          getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_OBLIGATOIRE'),
          postalCodeFranceMetro,
          postalCodeDROM
        )}
        displayErrorMessage={true}
        formatValue={formatZipCode}
        data-testid="ADHERENT_CODE_POSTAL"
        isDisplayed={
          getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <SimulationCustomerGranteesExtraInfoMessage
        isDisplayed={
          !form.getFieldState('customer.addresses.0.zipCode').invalid
        }
        zipCode={form.watch('customer.addresses.0.zipCode')}
      />

      <ControlledInput
        name="customer.addresses.0.fields.0"
        label={getMiddleText('ADHERENT_ADRESSE_LABEL_CHAMP1')}
        form={form}
        rules={isRequiredInput(
          getMiddleBoolean('ADHERENT_ADRESSE_CHAMP1_EST_OBLIGATOIRE')
        )}
        data-testid="ADHERENT_ADRESSE_CHAMP1"
        isDisplayed={
          getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_ADRESSE_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <ControlledInput
        name="customer.addresses.0.fields.1"
        label={getMiddleText('ADHERENT_ADRESSE_LABEL_CHAMP2')}
        form={form}
        rules={isRequiredInput(
          getMiddleBoolean('ADHERENT_ADRESSE_CHAMP2_EST_OBLIGATOIRE')
        )}
        data-testid="ADHERENT_ADRESSE_CHAMP2"
        isDisplayed={
          getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_ADRESSE_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <ControlledInput
        name="customer.addresses.0.fields.2"
        label={getMiddleText('ADHERENT_ADRESSE_LABEL_CHAMP3')}
        form={form}
        containerClassName="col-span-2"
        rules={isRequiredInput(
          getMiddleBoolean('ADHERENT_ADRESSE_CHAMP3_EST_OBLIGATOIRE')
        )}
        data-testid="ADHERENT_ADRESSE_CHAMP3"
        isDisplayed={
          getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_ADRESSE_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <ControlledSelect
        name="customer.addresses.0.city"
        label={getMiddleText('ADHERENT_VILLE_LABEL')}
        form={form}
        options={propOr([], 'cities', cities)}
        rules={isRequiredSelect(
          propOr([], 'cities', cities),
          getMiddleBoolean('ADHERENT_VILLE_EST_OBLIGATOIRE')
        )}
        data-testid="ADHERENT_VILLE"
        isDisplayed={
          getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_VILLE_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_VILLE_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />

      <ControlledDatePicker
        name="customer.birthDate"
        label={getMiddleText('ADHERENT_DATE_NAISSANCE_LABEL')}
        tooltip={customerAgeTooltipText}
        form={form}
        minDate={minBirthDate?.toDate()}
        maxDate={maxBirthDate?.toDate()}
        rules={dateHasConstraints(
          minBirthDate,
          maxBirthDate,
          getMiddleText('ADHERENT_DATE_NAISSANCE_AGE_MINIMUM_INFO', {
            minCustomerAge: minCustomerAge
          }),
          getMiddleText('ADHERENT_DATE_NAISSANCE_AGE_MAXIMUM_INFO', {
            maxCustomerAge: maxCustomerAge
          }),
          customerAgeInfoText,
          getMiddleBoolean('ADHERENT_DATE_NAISSANCE_EST_OBLIGATOIRE')
        )}
        data-testid="ADHERENT_DATE_NAISSANCE"
        isDisplayed={
          getMiddleBoolean('ADHERENT_DATE_NAISSANCE_EST_NECESSAIRE') &&
          getMiddleBoolean(
            'ADHERENT_DATE_NAISSANCE_EST_DEMANDE_DONNEES_CONTRAT'
          )
        }
      />
      <DashedTitle isDisplayed={displayAdditionalInfo}>
        {getMiddleText('ADHERENT_GENERAL_VIEW_SUBTITLE2')}
      </DashedTitle>
      <ControlledSelect
        name="customer.socialSecurities.0.regime"
        label={getMiddleText('ADHERENT_REGIME_LABEL')}
        rules={isRequiredSelect(
          propOr([], 'regimes', regimeObligatoire),
          getMiddleBoolean('ADHERENT_REGIME_EST_OBLIGATOIRE')
        )}
        form={form}
        options={propOr([], 'regimes', regimeObligatoire)}
        data-testid="ADHERENT_REGIME"
        isDisplayed={
          getMiddleBoolean('ADHERENT_REGIME_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_REGIME_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <ControlledPhoneInput
        name={'customer.phones.0'}
        label={getMiddleText('ADHERENT_TEL_MOBILE_LABEL')}
        form={form}
        rules={getMailMobileLandlineInputRules('mobile')}
        isInternational
        options={phoneIndicatives || []}
        data-testid="ADHERENT_TEL_MOBILE"
        isDisplayed={
          getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <ControlledPhoneInput
        name={'customer.phones.1'}
        isMobile={false}
        label={getMiddleText('ADHERENT_TEL_FIXE_LABEL')}
        form={form}
        rules={getMailMobileLandlineInputRules('landline')}
        data-testid="ADHERENT_TEL_FIXE"
        messageClassName="!mb-0"
        isDisplayed={
          getMiddleBoolean('ADHERENT_TEL_FIXE_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_TEL_FIXE_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />

      <ControlledCheckbox
        isDisplayed={
          aPhoneNumberHasAValueAndIsValid &&
          getMiddleBoolean('ADHERENT_CONSENTEMENT_TEL_EST_NECESSAIRE') &&
          (getMiddleBoolean(
            'ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_CONTRAT'
          ) ||
            getMiddleBoolean('ADHERENT_TEL_FIXE_EST_DEMANDE_DONNEES_CONTRAT'))
        }
        containerClassName="mt-[-20px] mb-4"
        labelClassNames="pl-4"
        name={'customer.GDPR.phone'}
        label={getExtraAdherentConsentementTelText(
          getMiddleText('ADHERENT_CONSENTEMENT_TEL_TEXT')
        )}
        form={form}
        rules={isRequiredBooleanTrue(
          (getMiddleBoolean('ADHERENT_CONSENTEMENT_TEL_EST_OBLIGATOIRE') &&
            !getMiddleBoolean(
              'ADHERENT_CONSENTEMENT_TEL_OU_EMAIL_EST_OBLIGATOIRE'
            )) ||
            (getMiddleBoolean(
              'ADHERENT_CONSENTEMENT_TEL_OU_EMAIL_EST_OBLIGATOIRE'
            ) &&
              !emailConsent)
        )}
      />
      <ControlledInput
        name="customer.emails.0.value"
        label={getMiddleText('ADHERENT_EMAIL_LABEL')}
        form={form}
        rules={getMailMobileLandlineInputRules('email')}
        data-testid="ADHERENT_EMAIL"
        messageClassName="!mb-0"
        displayErrorMessage={true}
        isDisplayed={
          getMiddleBoolean('ADHERENT_EMAIL_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_EMAIL_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <ControlledCheckbox
        isDisplayed={
          emailHasAValueAndIsValid &&
          getMiddleBoolean('ADHERENT_CONSENTEMENT_EMAIL_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_EMAIL_EST_DEMANDE_DONNEES_CONTRAT')
        }
        containerClassName="mt-[-20px] mb-4"
        labelClassNames="pl-4"
        name={'customer.GDPR.email'}
        label={getMiddleText('ADHERENT_CONSENTEMENT_EMAIL_TEXT')}
        form={form}
        rules={isRequiredBooleanTrue(
          (getMiddleBoolean('ADHERENT_CONSENTEMENT_EMAIL_EST_OBLIGATOIRE') &&
            !getMiddleBoolean(
              'ADHERENT_CONSENTEMENT_TEL_OU_EMAIL_EST_OBLIGATOIRE'
            )) ||
            (getMiddleBoolean(
              'ADHERENT_CONSENTEMENT_TEL_OU_EMAIL_EST_OBLIGATOIRE'
            ) &&
              !phoneConsent)
        )}
      />

      <ControlledSSInput
        label={getMiddleText('ADHERENT_NSS_LABEL')}
        SSInputName="customer.socialSecurities.0.number"
        birthDateInputName="customer.birthDate"
        form={form}
        mandatory={getMiddleBoolean('ADHERENT_NSS_EST_OBLIGATOIRE')}
        data-testid="ADHERENT_NSS"
        isDisplayed={
          getMiddleBoolean('ADHERENT_NSS_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_NSS_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <DashedTitle isDisplayed={displayActivityInfo}>
        {getMiddleText('ADHERENT_GENERAL_ACTIVITE_TITLE')}
      </DashedTitle>
      <ControlledSelect
        name="customer.CSPs.0.categoryLvl1CSPExternalId"
        label={getMiddleText('ADHERENT_SIT_PRO_LABEL')}
        rules={isRequiredSelect(
          professionalSituations,
          getMiddleBoolean('ADHERENT_SIT_PRO_EST_OBLIGATOIRE')
        )}
        form={form}
        options={professionalSituations || []}
        data-testid="ADHERENT_SIT_PRO"
        isDisplayed={
          getMiddleBoolean('ADHERENT_SIT_PRO_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_SIT_PRO_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <ControlledSelect
        name="customer.CSPs.0.categoryCSPExternalId"
        label={getMiddleText('ADHERENT_CSP_LABEL')}
        rules={isRequiredSelect(
          socioProfessionalCategories,
          getMiddleBoolean('ADHERENT_CSP_EST_OBLIGATOIRE')
        )}
        form={form}
        options={socioProfessionalCategories || []}
        data-testid="ADHERENT_CSP"
        isDisplayed={
          getMiddleBoolean('ADHERENT_CSP_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_CSP_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <ControlledSelect
        name="customer.CSPs.0.externalId"
        label={getMiddleText('ADHERENT_PROFESSION_LABEL')}
        rules={isRequiredSelect(
          filteredJobs,
          getMiddleBoolean('ADHERENT_PROFESSION_EST_OBLIGATOIRE')
        )}
        form={form}
        tooltip={getMiddleText('ADHERENT_PROFESSION_INFO')}
        options={filteredJobs || []}
        data-testid="ADHERENT_PROFESSION"
        isDisplayed={
          getMiddleBoolean('ADHERENT_PROFESSION_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_PROFESSION_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <ControlledInput
        name="customer.socialSecurities.0.regime"
        label={getMiddleText('ADHERENT_CAISSE_PRO_LABEL')}
        form={form}
        rules={isRequiredInput(
          getMiddleBoolean('ADHERENT_CAISSE_PRO_EST_OBLIGATOIRE')
        )}
        defaultValue={caisseProValue}
        disabled
        data-testid="ADHERENT_CAISSE_PRO"
        isDisplayed={
          getMiddleBoolean('ADHERENT_PROFESSION_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_PROFESSION_EST_DEMANDE_DONNEES_CONTRAT') &&
          getMiddleBoolean('ADHERENT_CAISSE_PRO_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_CAISSE_PRO_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <TwinRanksSelect
        name="customer.socialSecurities.0.twinsRank"
        label={getMiddleText('ADHERENT_RANG_LABEL')}
        tooltip={getMiddleText('ADHERENT_RANG_INFO_BULLE')}
        form={form}
        rules={getMiddleBoolean('ADHERENT_RANG_EST_OBLIGATOIRE')}
        data-testid="ADHERENT_RANG"
        isDisplayed={
          getMiddleBoolean('ADHERENT_RANG_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_RANG_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <ControlledRadioButton
        name="customer.professionalStatus"
        label={getMiddleText('ADHERENT_ACTIVITE_LABEL')}
        rules={isRequiredSelect(
          activities,
          getMiddleBoolean('ADHERENT_ACTIVITE_EST_OBLIGATOIRE')
        )}
        form={form}
        className={'grid gap-[10px]'}
        options={activities}
        data-testid="ADHERENT_ACTIVITE"
        isDisplayed={
          getMiddleBoolean('ADHERENT_ACTIVITE_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_ACTIVITE_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <SimulationCustomerGranteesExtraCompanyCreationDate form={form}/>
      <ControlledInput
        name="customer.finances.0.annualIncome"
        label={getMiddleText('ADHERENT_REVENU_ANNUEL_LABEL')}
        form={form}
        variant="number"
        displayErrorMessage={true}
        rules={
          equals(getMiddleText('ADHERENT_REVENU_ANNUEL_UNITE'), 'PASS')
            ? numberIsBetween(
              getMiddleNumber('ADHERENT_REVENU_ANNUEL_MIN') *
                  propOr(0, 'PASS', legalIndicators) +
                  1,
              getMiddleNumber('ADHERENT_REVENU_ANNUEL_MAX') *
                  propOr(0, 'PASS', legalIndicators),
              getMiddleText('ADHERENT_REVENU_ANNUEL_MESSAGE_ERREUR', {
                minAnnualRevenue: getMiddleNumber(
                  'ADHERENT_REVENU_ANNUEL_MIN'
                ),
                maxAnnualRevenue: getMiddleNumber(
                  'ADHERENT_REVENU_ANNUEL_MAX'
                ),
                annualRevenueUnit: getMiddleText(
                  'ADHERENT_REVENU_ANNUEL_UNITE'
                )
              }),
              getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_OBLIGATOIRE')
            )
            : isRequiredInput(
              getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_OBLIGATOIRE')
            )
        }
        append="€"
        data-testid="ADHERENT_REVENU_ANNUEL"
        isDisplayed={
          getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <InfoMessage
        iconName={'lightbulb'}
        isDisplayed={
          getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_DEMANDE_DONNEES_CONTRAT')
        }
      >
        {getMiddleText('ADHERENT_REVENU_ANNUEL_MESSAGE_INFO')}
      </InfoMessage>
      <ControlledRadioButton
        containerClassName="w-full"
        label={getMiddleText('ADHERENT_ID_TNS_LABEL')}
        id="customer.isTNS"
        name="customer.isTNS"
        booleanValue
        form={form}
        rules={isRequiredBoolean(
          getMiddleBoolean('ADHERENT_ID_TNS_EST_OBLIGATOIRE')
        )}
        options={booleanOptions}
        data-testid="ADHERENT_ID_TNS_LABEL"
        isDisplayed={
          getMiddleBoolean('ADHERENT_ID_TNS_EST_NECESSAIRE') &&
          getMiddleBoolean('ADHERENT_ID_TNS_EST_DEMANDE_DONNEES_CONTRAT')
        }
      />
      <CustomerIsCSS
        form={form}
        getMiddleBoolean={getMiddleBoolean}
        getMiddleText={getMiddleText}
      />
      <ControlledCheckbox
        isDisplayed={
          displayCommercialConsent &&
          isNotNilOrEmpty(
            getMiddleText('ADHERENT_CONSENTEMENT_COMMERCIAL_MENTION1')
          )
        }
        labelClassNames="pl-4"
        name={'customer.GDPR.company'}
        label={getMiddleText('ADHERENT_CONSENTEMENT_COMMERCIAL_MENTION1', {
          companyName: getMiddleText('GENERAL_CLIENT_NOM')
        })}
        form={form}
      />
      <ControlledCheckbox
        isDisplayed={
          displayCommercialConsent &&
          isNotNilOrEmpty(
            getMiddleText('ADHERENT_CONSENTEMENT_COMMERCIAL_MENTION2')
          )
        }
        labelClassNames="pl-4"
        name={'customer.GDPR.partners'}
        label={getMiddleText('ADHERENT_CONSENTEMENT_COMMERCIAL_MENTION2', {
          companyName: getMiddleText('GENERAL_CLIENT_NOM')
        })}
        form={form}
      />
      <Message
        className="w-full"
        variant={'error'}
        label={telEmailValidationErrorMessage}
      />
    </FormLayout>
  );
};

CustomerForm.propTypes = {
  jobs: PropTypes.array,
  professionalSituations: PropTypes.array,
  regimeObligatoire: PropTypes.object,
  socioProfessionalCategories: PropTypes.array,
  activities: PropTypes.array,
  getMiddleText: PropTypes.func,
  getMiddleBoolean: PropTypes.func,
  getMiddleNumber: PropTypes.func,
  phoneIndicatives: PropTypes.array,
  postalCodeFranceMetro: PropTypes.bool,
  postalCodeDROM: PropTypes.bool,
  selectedTab: PropTypes.number,
  displayAdditionalInfo: PropTypes.bool,
  displayActivityInfo: PropTypes.bool
};

export default CustomerForm;
